import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    bulkSetCatalogItemsActiveResultSchema,
    bulkSetCatalogItemsActiveSchema,
    createCatalogItemSchema,
    listCatalogItemsQuerySchema,
    updateCatalogItemSchema,
} from "@common/schemas/domains/catalogItem/catalogItem.schema";
import { CatalogItem, CatalogItemPrice } from "../types/catalogItem";

const c = initContract();

const WorkspacePathParamsSchema = z.object({
    workspaceId: z.string().uuid(),
});

const CatalogItemPathParamsSchema = z.object({
    workspaceId: z.string().uuid(),
    catalogItemId: z.string().uuid(),
});

export const catalogItemContract = c.router(
    {
        listCatalogItems: {
            method: "GET",
            path: "/:workspaceId",
            pathParams: WorkspacePathParamsSchema,
            query: listCatalogItemsQuerySchema,
            responses: {
                // `total`/`pageCount` are only meaningful when `page`/`limit` were
                // passed in the query; unpaginated callers can ignore them (they
                // reflect the full matching set either way).
                200: c.type<{
                    data: CatalogItem[];
                    total: number;
                    pageCount: number;
                }>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        // Idempotent find-or-create by normalized name within an entity.
        // Returns the existing active item (created: false) on a name
        // match/race instead of erroring.
        createCatalogItem: {
            method: "POST",
            path: "/:workspaceId",
            pathParams: WorkspacePathParamsSchema,
            body: createCatalogItemSchema,
            responses: {
                201: c.type<{ item: CatalogItem; created: boolean }>(),
                200: c.type<{ item: CatalogItem; created: boolean }>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateCatalogItem: {
            method: "PATCH",
            path: "/:workspaceId/:catalogItemId",
            pathParams: CatalogItemPathParamsSchema,
            body: updateCatalogItemSchema,
            responses: {
                // `createdPrices` maps each batched `prices` "create"
                // operation's client-supplied `clientId` to its resulting
                // persisted price, so a caller that staged local price rows
                // (client-only ids) can reconcile them against the real,
                // server-assigned prices now returned on `item.prices`.
                // Empty/omitted when the request had no create operations.
                200: c.type<{
                    item: CatalogItem;
                    createdPrices: {
                        clientId: string;
                        price: CatalogItemPrice;
                    }[];
                }>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        bulkSetCatalogItemsActive: {
            method: "PATCH",
            path: "/:workspaceId/bulk/active",
            pathParams: WorkspacePathParamsSchema,
            body: bulkSetCatalogItemsActiveSchema,
            responses: {
                200: bulkSetCatalogItemsActiveResultSchema,
                400: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        deleteCatalogItem: {
            method: "DELETE",
            path: "/:workspaceId/:catalogItemId",
            pathParams: CatalogItemPathParamsSchema,
            responses: {
                204: z.void(),
                409: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        duplicateCatalogItem: {
            method: "POST",
            path: "/:workspaceId/:catalogItemId/duplicate",
            pathParams: CatalogItemPathParamsSchema,
            body: z.object({}).optional(),
            responses: {
                201: c.type<{ item: CatalogItem }>(),
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        // Intentionally no single-price create/update/delete endpoints: every
        // price mutation goes through updateCatalogItem's batched `prices`
        // field so nothing can persist before the item's own Save (see
        // catalogItemPrice.schema.ts).
    },
    {
        pathPrefix: "/items",
    },
);
